import { Select, Spin, Flex, Avatar } from 'antd';
import React, { useState, useEffect, useCallback, useRef } from 'react';

import ninjaApi from 'store/redux/apis';

const ProductSelector = (props) => {
  const { value, onChange, ...restProps } = props;

  const [getProductsQuery, { isFetching }] = ninjaApi.useLazyGetProductsQuery();

  const [allProducts, setAllProducts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const PER_PAGE = 60;

  const loadingRef = useRef(false);
  const initialLoadRef = useRef(false);
  const initialValueLoadedRef = useRef(false);
  const searchTimeoutRef = useRef(null);

  useEffect(() => {
    if (value && value.length > 0 && !initialValueLoadedRef.current) {
      initialValueLoadedRef.current = true;
      loadInitialProducts(value);
    }
  }, [value]);

  useEffect(() => {
    if (!initialLoadRef.current) {
      initialLoadRef.current = true;
      loadProducts(1);
    }
  }, []);

  // handle search
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setAllProducts([]);
      setCurrentPage(1);
      setHasMore(true);
      loadProducts(1, searchTerm);
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm]);

  const loadInitialProducts = async (productIds) => {
    try {
      const result = await getProductsQuery({
        filters: { product_ids: productIds },
      }).unwrap();

      if (result?.data) {
        setAllProducts((prev) => {
          const existingIds = new Set(prev.map((p) => p.id));
          const newProducts = result.data.filter((p) => !existingIds.has(p.id));
          return [...newProducts, ...prev];
        });
      }
    } catch (error) {
      console.error(error);
    }
  };

  const loadProducts = async (page, search = '') => {
    if (loadingRef.current) return;

    loadingRef.current = true;
    setIsLoadingMore(true);

    try {
      const params = { page, per_page: PER_PAGE };

      if (search) {
        params.filters = { name: search };
      }

      const result = await getProductsQuery(params).unwrap();

      if (result?.data) {
        setAllProducts((prev) => {
          const existingIds = new Set(prev.map((p) => p.id));
          const newProducts = result.data.filter((p) => !existingIds.has(p.id));
          return page === 1 ? newProducts : [...prev, ...newProducts];
        });

        const pagination = result.pagination;
        if (pagination) {
          const hasMoreData = pagination.page < pagination.total_pages;
          setHasMore(hasMoreData);
        } else {
          setHasMore(result.data.length >= PER_PAGE);
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      loadingRef.current = false;
      setIsLoadingMore(false);
    }
  };

  const handleSearch = (value) => {
    setSearchTerm(value);
  };

  const handleDropdownVisibleChange = (open) => {
    if (!open && searchTerm) {
      setSearchTerm('');
      setAllProducts([]);
      setCurrentPage(1);
      setHasMore(true);
      loadProducts(1);
    }
  };

  const handlePopupScroll = useCallback(
    (e) => {
      const { target } = e;
      const threshold = 50;

      if (
        target.scrollTop + target.offsetHeight >= target.scrollHeight - threshold &&
        !loadingRef.current &&
        hasMore
      ) {
        const nextPage = currentPage + 1;
        setCurrentPage(nextPage);
        loadProducts(nextPage, searchTerm);
      }
    },
    [currentPage, hasMore, searchTerm]
  );

  return (
    <Select
      className='w-full'
      value={value}
      onChange={onChange}
      placeholder='Select a product'
      showSearch
      maxTagCount='responsive'
      mode='multiple'
      filterOption={false}
      onSearch={handleSearch}
      onDropdownVisibleChange={handleDropdownVisibleChange}
      onPopupScroll={handlePopupScroll}
      loading={isFetching && !isLoadingMore}
      notFoundContent={!isFetching && allProducts?.length === 0 && 'No products found'}
      dropdownRender={(menu) => (
        <>
          {menu}
          {isLoadingMore && hasMore && (
            <div style={{ textAlign: 'center', padding: '8px' }}>
              <Spin size='small' />
            </div>
          )}
        </>
      )}
      {...restProps}
    >
      {allProducts?.map((product, key) => (
        <Select.Option
          key={product.id || key}
          value={product.id}
          label={product.name || `Product ${product.id}`}
        >
          <Flex className='items-center w-full gap-2'>
            {product?.media_file?.url && (
              <Avatar
                src={product.media_file.url}
                alt='Product'
                className='flex-shrink-0 w-6 h-6 rounded-sm'
              />
            )}

            <span className='text-xs font-medium truncate min-w-0 flex-1'>
              {product.name || `Product ${product.id}`}
            </span>
          </Flex>
        </Select.Option>
      ))}
    </Select>
  );
};

export default ProductSelector;
